import React, { useState, useEffect, useCallback, useRef } from "react"

import {
  Box,
  Stack,
  Typography,
  CircularProgress,
  type BoxProps,
  useMediaQuery,
} from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useUrlParams } from "@ggs/hooks"

import useSearchForm from "../../local/hooks/useSearchForm"
import type { FC, ResponsiveImageType, iconKeys } from ".."
import { Container, Link, Pagination, ResponsiveImage, SvgIcon, Button } from ".."
import { suggestedItems } from "./suggestions"

type SearchResultItem = {
  title: string
  description: string
  image?: ResponsiveImageType
  uri: string
  type?: string
}

type Filter = {
  label: string
  value: string
  count: number
}

const SearchResults: FC = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const isBrowser = typeof window !== "undefined"
  const initialValue = isBrowser ? new URLSearchParams(window.location.search).get("keyword") : ""
  const [inputValue, setInputValue] = useState<string>(initialValue as string)
  const { getSearchResults } = useSearchForm()
  const { params, setParam } = useUrlParams(
    {
      keyword: "",
      filter: "all",
      page: 1,
    },
    "/search"
  )
  const currentPage = parseInt(params.page as string)
  const PAGE_SIZE = 14
  const [currentFilter, setCurrentFilter] = useState<string | null>(params.filter as string)
  const [searchResults, setSearchResults] = useState({
    totalResults: 0,
    pages: 1,
    results: [],
  })
  const [filters, setFilters] = useState([])
  const [filterCounters, setFilterCounters] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  const onHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value)
  }

  const fetchSearchResults = async (
    keyword: string,
    pageIndex: number,
    filter: string | null = null,
    includeLoader: boolean = true
  ) => {
    if (includeLoader) setIsLoading(true)
    const pageOffset = pageIndex || 0
    const filterValue = filter === "all" ? null : filter
    try {
      const response = await getSearchResults(keyword, pageOffset, filterValue as any)
      setSearchResults((results: any) => ({ ...results, ...response }))
      setParam("keyword", keyword)
      if (filter) {
        setParam("filter", filter)
      } else {
        setParam("filter", null)
      }
      setFilters(response.filters as any)
    } catch (error) {
      console.error("Failed to fetch search results:", error)
    } finally {
      setIsLoading(false)
    }
  }

  const onHandleSearch = () => {
    setParam("keyword", inputValue)
    setParam("page", 1)
    setParam("filter", "all")
    setCurrentFilter("all")
    fetchSearchResults(inputValue, 0, "all")
  }

  const fetchFilterCounters = () => {
    getSearchResults(params.keyword as string, 0).then((response) => {
      setFilterCounters(response.filters as any)
    })
  }

  const onPageChange = useCallback(
    (page: number) => {
      const pageIndex = Math.max((page - 1) * PAGE_SIZE, 0)
      setParam("page", `${page}`)
      fetchSearchResults(params.keyword as string, pageIndex, params.filter as string)
    },
    [params]
  )

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      onHandleSearch()
    }
  }

  const onHandleFilter = (filter: string) => {
    setCurrentFilter(filter)
    setParam("filter", filter)
    setParam("page", "1")
    fetchSearchResults(params.keyword as string, 0, filter, false)
  }

  useEffect(() => {
    const pageIndex = params.page ? Math.max(((params.page as number) - 1) * PAGE_SIZE, 0) : 0
    fetchSearchResults(params.keyword as string, pageIndex, params.filter as string)
    fetchFilterCounters()
  }, [])

  useEffect(() => {
    fetchFilterCounters()
  }, [params.keyword])

  return (
    <Container>
      <InputField
        icon="search"
        placeholder="Search for trips and places to visit"
        value={inputValue}
        onChange={onHandleChange}
        onHandleSearch={onHandleSearch}
        onClearInput={() => setInputValue("")}
        onKeyDown={handleKeyDown}
      />
      {isLoading ? (
        <Box display="flex" alignItems="center" justifyContent="center" minHeight="50vh">
          <CircularProgress />
        </Box>
      ) : (
        <Box mt={{ xs: "30px", md: "60px" }}>
          <ResultsHeader
            keyword={params.keyword as string}
            hasResults={Boolean(searchResults.totalResults)}
          />
          <Box
            display="grid"
            gridTemplateColumns={{ xs: "1fr", md: "max-content 1fr" }}
            mt={{ xs: "30px", md: "60px" }}
          >
            {isMobile ? (
              <FiltersWrapperMobile
                activeFilter={currentFilter as string}
                setActiveFilter={setCurrentFilter}
                filters={filters}
                counters={filterCounters}
                onSetFilter={onHandleFilter}
              />
            ) : (
              <FiltersWrapperDesktop
                activeFilter={currentFilter as string}
                setActiveFilter={setCurrentFilter}
                filters={filters}
                counters={filterCounters}
                onSetFilter={onHandleFilter}
              />
            )}
            {Boolean(searchResults.totalResults) ? (
              <SearchResultItems results={searchResults.results} />
            ) : (
              <SearchResultItems results={suggestedItems as any} />
            )}
          </Box>
          <Box pt={{ xs: 15, md: 20 }} pb={{ xs: 15, md: 45 }}>
            {searchResults.pages > 1 && (
              <Pagination
                count={searchResults.pages}
                page={currentPage}
                onPageChange={onPageChange}
              />
            )}
          </Box>
        </Box>
      )}
    </Container>
  )
}
export default SearchResults

// A sub component for the Input field
type InputFieldProps = {
  icon: string
  onClearInput: () => void
  onHandleSearch: () => void
} & BoxProps<"input">

const InputField: FC<InputFieldProps> = ({
  icon,
  onClearInput,
  value,
  onChange,
  onHandleSearch,
  onKeyDown,
  ...props
}) => {
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])
  return (
    <Box
      position="relative"
      sx={(theme) => ({
        maxWidth: 1510,
        width: { xs: "100%", lg: "calc(100vw - 50px)" },
        border: `1px solid ${theme.palette["neutral.200"].main}`,
        bgcolor: theme.palette["neutral.100"].main,
        borderRadius: { xs: 1.5, md: 2 },
        boxShadow: `0px 0px 25px 0px rgba(0, 0, 0, 0.10)`,
        display: "flex",
        alignItems: "center",
        transform: {
          xs: `translateY(${theme.spacing(-7.75)})`,
          md: `translateY(${theme.spacing(-11)})`,
        },
        mb: {
          xs: -7.75,
          md: -11,
        },
      })}
    >
      <Box
        component="input"
        type="text"
        autoFocus
        value={value}
        onChange={onChange}
        onKeyDown={onKeyDown}
        sx={(theme) => ({
          ...theme.typography["body.lg"],
          "border": 0,
          "borderRadius": { xs: 1.5, md: 2 },
          "bgcolor": theme.palette["neutral.100"].main,
          "color": theme.palette["neutral.900"].main,
          "fontWeight": 400,
          "height": { xs: 56, md: 77 },
          "opacity": 0.7,
          "p": { xs: 2, md: 4 },
          "pl": { xs: 11, md: 16.5 },
          "width": "100%",
          "&::placeholder": {
            color: "rgba(55, 54, 56, 0.60)",
            fontWeight: 400,
            opacity: 0.7,
          },

          "&:focus": {
            outline: "none",
          },
        })}
        {...props}
      />
      <SvgIcon
        variant={icon as iconKeys}
        size={{ xs: 16, md: 20 }}
        strokeMode
        sx={{
          position: "absolute",
          left: { xs: 15, md: 25 },
          top: "50%",
          transform: "translateY(-50%)",
          stroke: (theme) => theme.palette["neutral.800"].main,
          zIndex: 1,
        }}
      />
      <Box
        role="button"
        tabIndex={0}
        onClick={onClearInput}
        display="flex"
        sx={{ cursor: "pointer" }}
      >
        <SvgIcon
          variant={"close"}
          size={12}
          sx={{
            position: "absolute",
            right: { xs: 100, md: 150 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 1,
          }}
        />
      </Box>
      <Button
        sx={{
          bgcolor: "#DAE7F1",
          minHeight: { xs: "40px!important", md: "53px!important" },
          px: { xs: 4.5, md: 8 },
          py: { md: 2 },
          mr: { xs: 2, md: 3 },
        }}
        onClick={onHandleSearch}
      >
        Search
      </Button>
    </Box>
  )
}

// A sub component for the results header
const ResultsHeader: FC<{ keyword: string; hasResults: boolean }> = ({
  keyword = null,
  hasResults = false,
}) => {
  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={6}
      alignItems="center"
      textAlign="center"
      px={4}
      maxWidth={1000}
      margin="0 auto"
    >
      {/* <Typography
        component="h3"
        variant="heading.3xl"
        sx={{
          mb: 2,
          fontSize: { xs: "28px", md: "92px" },
        }}
        color={(theme) => theme.palette["neutral.700"].main}
      >
        {hasResults ? "Search Results" : "No Search Results"}
      </Typography> */}
      <Typography variant="body.lg">
        {hasResults ? (
          <>
            {keyword && (
              <Typography variant="body.lg" component="span">
                Results for &apos;{keyword}&apos; on Goway Travel
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body.lg" component="span">
            We didn&apos;t find anything matching{" "}
            <span style={{ color: "#CE491C", fontWeight: 500 }}>&apos;{keyword}&apos;.</span> We
            invite you to explore some of our most popular destinations and trips listed below. We
            hope you find what you&apos;re looking for!
          </Typography>
        )}
      </Typography>
    </Box>
  )
}

// A sub component for the filters
const FiltersWrapperDesktop: FC<{
  activeFilter: string
  setActiveFilter: (filter: string) => void
  filters: Filter[]
  counters: Filter[]
  onSetFilter: (filter: string) => void
}> = ({ activeFilter = "all", filters = [], counters, onSetFilter, setActiveFilter }) => {
  const isActive = (filter: string) => {
    return filter === activeFilter
  }
  const getCounterNumber = (filter: string) => {
    return counters.find((filterCounter) => filterCounter.label === filter)?.count
  }
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        gap: 7,
        pr: { md: 5, lg: 20 },
        borderRight: `1px solid ${theme.palette["neutral.300"].main}`,
      })}
    >
      <Typography
        variant="body.md"
        color={(theme) => theme.palette["neutral.700"].main}
        sx={{ opacity: 0.7 }}
      >
        Categories
      </Typography>
      <Stack spacing={2}>
        {filters.map((filter, index) => {
          const counter = getCounterNumber(filter.label)
          if (counter === 0) return null
          return (
            <Box
              key={index}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault()
                  setActiveFilter(filter.value)
                  onSetFilter(filter.value)
                }
              }}
              onClick={() => {
                setActiveFilter(filter.value)
                onSetFilter(filter.value)
              }}
              sx={(theme) => ({
                "display": "flex",
                "alignItems": "center",
                "height": 52,
                "padding": theme.spacing(4.5, 5.5),
                "borderRadius": 999,
                "transition": theme.transitions.create(["border-color"]),
                "gap": 3,
                "width": "fit-content",
                "border": `1px solid ${
                  isActive(filter.value)
                    ? theme.palette["primary.800"].main
                    : theme.palette["neutral.200"].main
                }`,
                "&:hover": {
                  borderColor: {
                    xs: theme.palette["primary.800"].main,
                    md: theme.palette["primary.400"].main,
                  },
                },
              })}
            >
              <Typography
                variant="body.md"
                color={(theme) =>
                  isActive(filter.value)
                    ? theme.palette["neutral.800"].main
                    : "rgba(55, 54, 56, 0.90)"
                }
                fontWeight={isActive(filter.value) ? 500 : 400}
              >
                {filter.label}
              </Typography>
              <Typography
                variant="body.md"
                color={(theme) => theme.palette["neutral.800"].main}
                sx={{ opacity: 0.5 }}
              >
                {`(${counter})`}
              </Typography>
            </Box>
          )
        })}
      </Stack>
    </Box>
  )
}

// A sub component for the filters on mobile
const FiltersWrapperMobile: FC<{
  activeFilter: string
  setActiveFilter: (filter: string) => void
  filters: Filter[]
  counters: Filter[]
  onSetFilter: (filter: string) => void
}> = ({ activeFilter = "all", filters = [], counters, onSetFilter, setActiveFilter }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const isActive = (filter: string) => {
    return filter === activeFilter
  }
  const getCounterNumber = (filter: string) => {
    return counters.find((filterCounter) => filterCounter.label === filter)?.count
  }

  return (
    <Box position="relative" margin={(theme) => theme.spacing(0, 1, 5)}>
      <Box
        role="button"
        tabIndex={0}
        aria-expanded={isDropdownOpen}
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            // Toggles dropdown on 'Enter' or 'Space' key press
            e.preventDefault() // Prevents scrolling for space key
            setIsDropdownOpen(!isDropdownOpen)
          }
        }}
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          px: 3.5,
          height: 52,
          borderRadius: 1.5,
          border: `2px solid ${theme.palette["neutral.200"].main}`,
          backgroundColor: theme.palette["neutral.100"].main,
        })}
      >
        <Typography variant="link.lg">Categories</Typography>
        <SvgIcon
          variant="dropdownIcon"
          viewBox="0 0 16 16"
          size={16}
          sx={{
            position: "absolute",
            transition: "transform 0.2s",
            right: 14,
            top: "50%",
            transform: `translateY(-50%) rotate(${isDropdownOpen ? "0deg" : "180deg"})`,
            zIndex: 1,
          }}
        />
      </Box>
      {isDropdownOpen && (
        <Box
          sx={(theme) => ({
            position: "absolute",
            top: 55,
            left: 0,
            width: "100%",
            bgcolor: "rgba(255, 255, 255, 0.98)",
            boxShadow: "0px 0px 25px 0px rgba(0, 0, 0, 0.10)",
            backdropFilter: "blur(10px)",
            borderRadius: 2,
            padding: { xs: 2, md: theme.spacing(2, 5) },
            zIndex: 2,
          })}
        >
          {filters.map((filter, index) => {
            const counter = getCounterNumber(filter.label)
            if (counter === 0) return null
            return (
              <Box
                key={index}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault() // Prevents scrolling for space key
                    setActiveFilter(filter.value)
                    onSetFilter(filter.value)
                    setIsDropdownOpen(false)
                  }
                }}
                onClick={() => {
                  setActiveFilter(filter.value)
                  onSetFilter(filter.value)
                  setIsDropdownOpen(false)
                }}
                p={3}
                sx={(theme) => ({
                  transition: theme.transitions.create(["background-color", "color", "opacity"]),
                  height: 54,
                  display: "flex",
                  alignItems: "center",
                  px: 4,
                  borderRadius: 1.5,
                  cursor: "pointer",
                  position: "relative",
                  gap: 3,
                  bgcolor: isActive(filter.value) ? "rgba(35, 53, 88, 0.06)" : "transparent",
                })}
              >
                <Typography
                  variant="link.lg"
                  sx={(theme) =>
                    isActive(filter.value)
                      ? {
                          color: theme.palette["secondary.800"].main,
                          opacity: 1,
                        }
                      : {
                          color: theme.palette["neutral.800"].main,
                          opacity: 0.7,
                        }
                  }
                >
                  {filter.label}
                </Typography>
                <Typography
                  variant="link.xs"
                  sx={(theme) => ({ color: theme.palette["neutral.800"].main, opacity: 0.48 })}
                >
                  {`(${counter})`}
                </Typography>
              </Box>
            )
          })}
        </Box>
      )}
    </Box>
  )
}

// A sub component for the results
const SearchResultItems: FC<{ results: SearchResultItem[] }> = ({ results }) => {
  const formatType = (type: string) => {
    if (type) {
      return type.replace(/_/g, " ")
    }
    return null
  }
  return (
    <Stack
      sx={(theme) => ({
        gap: { xs: 1, md: 5 },
        paddingLeft: { xs: 0, md: theme.spacing(5), lg: theme.spacing(20) },
        display: "grid",
        gridTemplateColumns: { xs: "1fr", xl: "repeat(2, 1fr)" },
      })}
    >
      {results.map((result, index) => (
        <Box
          key={index}
          position="relative"
          sx={(theme) => ({
            "borderRadius": 1.5,
            "transition": theme.transitions.create(["box-shadow"]),
            ":hover": {
              boxShadow: "1px 1px 10px 0px rgba(0, 0, 0, 0.15)",
            },
          })}
        >
          <Link type="overlay" href={result.uri} aria-label={result.title} title={result.title} />
          <Box
            key={index}
            sx={(theme) => ({
              display: "flex",
              gap: { xs: 5, md: 12 },
              alignItems: { xs: result?.description ? "flex-start" : "center", md: "center" },
              padding: { xs: theme.spacing(4, 1), md: 4 },
            })}
          >
            <ResponsiveImage
              image={result.image}
              alt={result.title}
              borderRadius={1.5}
              sx={{
                width: { xs: 70, md: 200 },
                height: { xs: 70, md: 140 },
              }}
            />
            <Stack gap={1}>
              {result.type && (
                <Box display="flex" alignItems="center" gap={1.5}>
                  {result.type === "trip" && (
                    <SvgIcon
                      variant="location"
                      size={{ xs: 10, md: 12 }}
                      sx={(theme) => ({
                        stroke: theme.palette["primary.800"].main,
                        fill: theme.palette["primary.800"].main,
                      })}
                    />
                  )}
                  {result.type === "suggested" && (
                    <SvgIcon
                      variant="fullStar"
                      size={{ xs: 10, md: 12 }}
                      sx={(theme) => ({
                        stroke: theme.palette["primary.800"].main,
                        fill: theme.palette["primary.800"].main,
                      })}
                    />
                  )}
                  <Typography
                    variant="body.md"
                    sx={{
                      color: "rgba(55, 54, 56, 0.70)",
                      textTransform: "uppercase",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    {formatType(result.type)}
                  </Typography>
                </Box>
              )}
              <Typography variant="heading.sm" sx={{ color: "neutral.800" }}>
                {result.title}
              </Typography>
              <Typography
                variant="body.md"
                sx={{ color: "neutral.800", opacity: 0.7, lineHeight: "1.4" }}
              >
                {result.description}
              </Typography>
            </Stack>
          </Box>
        </Box>
      ))}
    </Stack>
  )
}
